import { useRouterState } from '@tanstack/react-router'
import { useCallback, useEffect, useEffectEvent, useRef, useState } from 'react'
import { io } from 'socket.io-client'
import type { Socket } from 'socket.io-client'
import type { CurrentUserWithPermissions } from '@/shared/lib/workspace-permissions'
import type { WorkspacePresenceUser } from '@/features/workspace-presence/model/workspace-presence'
import type { PresenceSocketContext } from '@/features/workspace-presence/model/presence-socket'
import { env } from '@/env'
import { CUSTOMER_AUTH_SESSION_EXPIRED_EVENT } from '@/shared/lib/customer-auth'
import { usePresenceRecordContext } from '@/features/workspace-presence/model/record-presence-context'
import {
  PRESENCE_DISCONNECT_STALE_MS,
  PRESENCE_HEARTBEAT_MS,
  decorateWorkspacePresence,
  getCurrentDocumentCollectionId,
} from '@/features/workspace-presence/model/workspace-presence'
import {
  buildPresenceSocketOptions,
  buildPresenceSocketUrl,
  registerPresenceSocketHandlers,
} from '@/features/workspace-presence/model/presence-socket'

export {
  buildPresenceSocketOptions,
  buildPresenceSocketUrl,
} from '@/features/workspace-presence/model/presence-socket'

type CurrentUser = CurrentUserWithPermissions

export function createSessionId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export function buildPresenceStatus() {
  if (document.visibilityState !== 'visible' || !navigator.onLine) {
    return 'away' as const
  }

  return 'online' as const
}

export function registerPresenceBrowserListeners(input: {
  sendHeartbeat: () => void
  setPresenceUsers: React.Dispatch<
    React.SetStateAction<Array<WorkspacePresenceUser>>
  >
}) {
  function handleVisibilityChange() {
    input.sendHeartbeat()
  }

  function handleOnline() {
    input.sendHeartbeat()
  }

  function handleOffline() {
    input.sendHeartbeat()
    input.setPresenceUsers([])
  }

  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('online', handleOnline)
  window.addEventListener('offline', handleOffline)

  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    window.removeEventListener('online', handleOnline)
    window.removeEventListener('offline', handleOffline)
  }
}

function usePresenceSocketLifecycle(input: {
  canConnect: boolean
  currentUser: CurrentUser | null
  heartbeatMs: number
  sendHeartbeat: () => void
  sessionIdRef: React.RefObject<string>
  setPresenceUsers: React.Dispatch<
    React.SetStateAction<Array<WorkspacePresenceUser>>
  >
  socketContextRef: React.RefObject<PresenceSocketContext>
  socketRef: React.RefObject<Socket | null>
}) {
  const {
    canConnect,
    currentUser,
    heartbeatMs,
    sendHeartbeat,
    sessionIdRef,
    setPresenceUsers,
    socketContextRef,
    socketRef,
  } = input

  useEffect(() => {
    if (!currentUser || !canConnect) {
      setPresenceUsers([])
      socketRef.current?.disconnect()
      socketRef.current = null

      return
    }

    const activeCurrentUser = currentUser
    const sessionId = sessionIdRef.current
    const socket = io(
      buildPresenceSocketUrl(env.VITE_API_URL),
      buildPresenceSocketOptions(),
    )

    socketRef.current = socket

    const removeSocketHandlers = registerPresenceSocketHandlers({
      currentUser: activeCurrentUser,
      disconnectStaleMs: PRESENCE_DISCONNECT_STALE_MS,
      sessionId,
      socketContextRef,
      setPresenceUsers,
      socket,
    })
    const removeBrowserListeners = registerPresenceBrowserListeners({
      sendHeartbeat,
      setPresenceUsers,
    })
    const removeSessionExpiryListener = registerSessionExpiryListener({
      setPresenceUsers,
      socket,
    })
    const stopHeartbeat = startPresenceHeartbeat(sendHeartbeat, heartbeatMs)
    socket.connect()

    return () => {
      socket.emit('presence.leave', {
        sessionId,
        workspaceId: activeCurrentUser.workspaceId,
      })
      removeSocketHandlers()
      removeBrowserListeners()
      removeSessionExpiryListener()
      socket.disconnect()
      if (socketRef.current === socket) {
        socketRef.current = null
      }
      stopHeartbeat()
    }
  }, [
    canConnect,
    currentUser,
    heartbeatMs,
    sendHeartbeat,
    sessionIdRef,
    setPresenceUsers,
    socketContextRef,
    socketRef,
  ])
}

function startPresenceHeartbeat(
  sendHeartbeat: () => void,
  heartbeatMs: number,
) {
  const intervalId = window.setInterval(sendHeartbeat, heartbeatMs)

  return () => window.clearInterval(intervalId)
}

function registerSessionExpiryListener({
  setPresenceUsers,
  socket,
}: {
  setPresenceUsers: React.Dispatch<
    React.SetStateAction<Array<WorkspacePresenceUser>>
  >
  socket: Socket
}) {
  const handleSessionExpired = () => {
    setPresenceUsers([])
    socket.disconnect()
  }

  window.addEventListener(
    CUSTOMER_AUTH_SESSION_EXPIRED_EVENT,
    handleSessionExpired,
  )

  return () =>
    window.removeEventListener(
      CUSTOMER_AUTH_SESSION_EXPIRED_EVENT,
      handleSessionExpired,
    )
}

function usePresenceContextSync(input: {
  canConnect: boolean
  currentDocumentCollectionId: string | null
  currentUser: CurrentUser | null
  pathname: string
  recordId: string | null
  sessionIdRef: React.RefObject<string>
  socketRef: React.RefObject<Socket | null>
}) {
  const syncContext = useEffectEvent(() => {
    const socket = input.socketRef.current

    if (!socket?.connected || !input.currentUser || !input.canConnect) {
      return
    }

    socket.emit('presence.updateContext', {
      documentCollectionId: input.currentDocumentCollectionId,
      recordId: input.recordId,
      route: input.pathname,
      sessionId: input.sessionIdRef.current,
      workspaceId: input.currentUser.workspaceId,
    })
  })

  useEffect(() => {
    syncContext()
  }, [input.currentDocumentCollectionId, input.pathname, input.recordId])
}

export function useWorkspacePresence({
  currentUser,
}: {
  currentUser: CurrentUser | null
}) {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })
  const recordPresenceContext = usePresenceRecordContext()
  const currentDocumentCollectionId =
    recordPresenceContext.documentCollectionId ??
    getCurrentDocumentCollectionId(pathname)
  const [presenceUsers, setPresenceUsers] = useState<
    Array<WorkspacePresenceUser>
  >([])
  const socketRef = useRef<Socket | null>(null)
  const socketContextRef = useRef<PresenceSocketContext>({
    currentDocumentCollectionId,
    currentUser,
    pathname,
    recordId: recordPresenceContext.recordId,
  })
  const sessionIdRef = useRef(createSessionId())
  const canConnect =
    env.VITE_PRESENCE_ENABLED &&
    (currentUser?.can('create', 'records') ?? false)
  const heartbeatMs = env.VITE_PRESENCE_HEARTBEAT_MS ?? PRESENCE_HEARTBEAT_MS

  socketContextRef.current = {
    currentDocumentCollectionId,
    currentUser,
    pathname,
    recordId: recordPresenceContext.recordId,
  }

  const sendHeartbeat = useCallback(() => {
    const socket = socketRef.current
    const activeCurrentUser = socketContextRef.current.currentUser

    if (!socket?.connected || !activeCurrentUser || !canConnect) {
      return
    }

    socket.emit('presence.heartbeat', {
      sessionId: sessionIdRef.current,
      status: buildPresenceStatus(),
      workspaceId: activeCurrentUser.workspaceId,
    })
  }, [canConnect])

  usePresenceSocketLifecycle({
    canConnect,
    currentUser,
    heartbeatMs,
    sendHeartbeat,
    sessionIdRef,
    setPresenceUsers,
    socketContextRef,
    socketRef,
  })

  usePresenceContextSync({
    canConnect,
    currentDocumentCollectionId,
    currentUser,
    pathname,
    recordId: recordPresenceContext.recordId,
    sessionIdRef,
    socketRef,
  })

  return decorateWorkspacePresence({
    currentDocumentCollectionId,
    presenceUsers,
  })
}

export type { OnlineWorkspacePresence } from '@/features/workspace-presence/model/workspace-presence'
